'use client';

import { jwtNeedsRefresh } from '@/core/http/app-session-expiry';
import { loadGQL } from '@/core/queries/gql-loader';
import { clearSessionCache, getClientSessionData } from '@/core/utils/auth-utils';
import { isExpiredJwtAuthBody } from '@/core/utils/is-expired-jwt-error';
import { getTokenRefreshManager } from '@/core/utils/token-refresh-manager';

import { updateAppSession } from '../app-api';

/**
 * SSE-side counterpart to the Axios interceptor's 401 handler. The
 * `yourAiSseStream` helper drives raw fetch (EventSource doesn't
 * support POST + auth headers), so the Axios refresh path doesn't
 * apply — we have to detect expiration and refresh manually.
 *
 * Body shapes that signal expiration (see `is-expired-jwt-error.ts`):
 *   - Asset-manager: `{ code: 'TOKEN_EXPIRED', message: 'Token expired', err: { message: 'jwt expired' } }`
 *   - Internal APIs: `{ err: { message: 'jwt expired' } }`
 *   - YouRAI/FastAPI: `{ detail: 'Signature has expired: ...' }`
 *
 * Returns `true` when the response body indicates an expired token
 * and the FE should attempt a refresh + retry; `false` otherwise so
 * the original 401 propagates as a hard auth failure.
 */
export function isExpiredAuthBody(body: string): boolean {
  return isExpiredJwtAuthBody(body);
}

/**
 * Refresh the current session via the same `getNewJwt` + manager path
 * the Axios interceptor uses (`http-client.ts:159-243`). Coordinated
 * across concurrent callers via the per-user
 * `tokenRefreshManager` so a parallel SSE + REST burst refreshes
 * once, not N times.
 *
 * Returns `true` once the new JWT is in `appSession`; `false` when
 * the refresh fails (caller should propagate the original 401 — the
 * full Axios path will pick up the failure on its own and trigger
 * the auth-expired flow). Doesn't handle the guest re-mint case;
 * SSE consumers run post-auth so guest expiry on a stream is rare
 * and can fall through to the Axios interceptor on the next REST
 * call.
 */
export async function refreshExpiredYourAiSession(): Promise<boolean> {
  const session = await getClientSessionData();
  if (!session) return false;
  const { userId, tenantId, refreshToken, jwt } = session;
  if (!tenantId || !refreshToken) return false;

  const manager = getTokenRefreshManager(userId, tenantId);
  return manager.executeRefresh(async () => {
    try {
      // `getNewJwt` returns the loose Apollo response shape; the Axios
      // interceptor leans on `any` here and we mirror that — typing the
      // returned envelope precisely would mean importing Apollo types
      // into the SSE module, which isn't worth the coupling for a path
      // that only reads two fields off `getJwtTokenV2`.
      const { getNewJwt } = await loadGQL();
      const accessToken = (await getNewJwt(tenantId, refreshToken, jwt)) as
        | { data?: { getJwtTokenV2?: { jwt?: string; refreshToken?: string } } }
        | null
        | undefined;
      const fresh = accessToken?.data?.getJwtTokenV2;
      if (!fresh?.jwt || !fresh.refreshToken) return false;
      await updateAppSession({ jwt: fresh.jwt, refreshToken: fresh.refreshToken });
      clearSessionCache();
      return true;
    } catch {
      return false;
    }
  });
}

/**
 * Refresh this browser's session before a call that sends its JWT through a route handler where an
 * expired one isn't refused but read as nobody: a story link's resolve then offers a signed-in reader
 * "Sign in to ask". Nothing when signed out or when the JWT is fresh; never throws, so the call goes
 * ahead as it would have.
 */
export async function refreshAppSessionIfExpiring(now = Date.now()): Promise<void> {
  try {
    const session = await getClientSessionData();
    const jwt = typeof session?.jwt === 'string' ? session.jwt : '';
    if (jwt && session?.refreshToken && jwtNeedsRefresh(jwt, now)) await refreshExpiredYourAiSession();
  } catch {
    // The call goes ahead with the session as it is.
  }
}
